import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import Icon from '@components/Icon/Icon';
import {
  BottomText,
  HorizontalFlex,
  MainText,
  PicBox,
  SubText,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';

import { RECORD_NAMES } from '@constants/urls-and-types';

import { useIsMobile } from '@hooks/useIsMobile';

import { resolveIcon } from '@utils/recordIcons';

import RecordTimelineCategory from '../RecordTimelineCategory/RecordTimelineCategory';
import {
  InfoTextBox,
  InnerContainer,
  OuterContainer,
  StatusContainer,
  Date,
} from './RecordTimelineCard.style';

const RecordTimelineCard: React.FC<{
  /**
   * Text on top of the card's body content
   */
  topText?: string;
  /**
   * Main text of the card's body content
   */
  text?: string;
  /**
   * Title of the card
   */
  title?: string | null;
  /**
   * Text on the bottom of the card's body content
   */
  bottomText?: string;
  /**
   * Colour of the card background
   */
  color?: string;
  /**
   * Does the record have notes? If true, shows a note icon
   */
  noteDisplay?: boolean;
  /**
   * Callback for clicking on the card
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Has the record been viewed? If true, shows a blue dot
   */
  viewed?: boolean;
  /**
   * Is the card clickable? If true, allows the card to be clicked
   */
  clickable?: boolean;
  /**
   * Survey Id of the card's record
   */
  surveyId: keyof typeof RECORD_NAMES;
  /**
   * DOM components to show in the card's body
   */
  children?: React.ReactNode;
  /**
   * Date of the record
   */
  date?: string;
  /**
   * Should the date be aligned to the right of the card?
   */
  right?: boolean;
}> = ({
  topText = '',
  text = '',
  title = '',
  bottomText = '',
  color,
  noteDisplay = false,
  onClick,
  viewed = true,
  clickable = true,
  surveyId,
  children = null,
  date,
  right = true,
}) => {
  const mobile = useIsMobile('md');

  const { palette } = useTheme();
  const resolvedColor = color ?? palette.background.paper;

  function handleChange(event: React.MouseEvent<HTMLDivElement>) {
    if (onClick) {
      onClick(event);
    }
  }

  const hasContent =
    children !== null ||
    noteDisplay ||
    topText !== '' ||
    text !== '' ||
    bottomText !== '';

  const theme = useTheme();

  return (
    <OuterContainer
      clickable={clickable}
      color={resolvedColor}
      data-testid="timeline-card-container"
      onClick={handleChange}
      paddingTop={mobile ? 25 : 15}
    >
      {mobile ? (
        <>
          <Date right={right}>{date}</Date>
          <HorizontalFlex marginBottom={0} marginTop={0}>
            <VerticalFlex alignItems="flex-start">
              <Typography
                color={palette.text.secondary}
                textAlign="center"
                variant="caption"
              >
                {RECORD_NAMES[surveyId]}
              </Typography>
            </VerticalFlex>
          </HorizontalFlex>
          <HorizontalFlex border={hasContent} marginBottom={0} marginTop={0}>
            <VerticalFlex alignItems="flex-start">
              <RecordTimelineCategory
                text={title}
                {...resolveIcon(surveyId, theme)}
              />
            </VerticalFlex>
          </HorizontalFlex>
        </>
      ) : (
        <HorizontalFlex border={hasContent} marginBottom={0} marginTop={0}>
          <VerticalFlex alignItems="flex-start">
            <RecordTimelineCategory
              text={title}
              {...resolveIcon(surveyId, theme)}
            />
          </VerticalFlex>
          <VerticalFlex alignItems="flex-end">
            <Typography
              color={palette.text.secondary}
              textAlign="center"
              variant={'body2'}
            >
              {RECORD_NAMES[surveyId]}
            </Typography>
          </VerticalFlex>
        </HorizontalFlex>
      )}
      {hasContent ? (
        <HorizontalFlex marginBottom={0} marginTop={0}>
          <VerticalFlex alignItems="flex-start">
            <InnerContainer>
              {!viewed && <StatusContainer />}
              <InfoTextBox>
                <SubText color={palette.text.secondary}>{topText}</SubText>
                <MainText>{text}</MainText>
                <BottomText>{bottomText}</BottomText>
                {children}
              </InfoTextBox>
            </InnerContainer>
          </VerticalFlex>
          <VerticalFlex alignItems="flex-end">
            <PicBox>
              {noteDisplay ? (
                <Icon color="text.secondary" name="file-lines" />
              ) : null}
            </PicBox>
          </VerticalFlex>
        </HorizontalFlex>
      ) : null}
    </OuterContainer>
  );
};

export default RecordTimelineCard;
